<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import dayjs from 'dayjs';
import store from '@/store';
import globalShared from '@/shared';
import { findApprovedOrgStickers, findGlows, benevityCauses as benevityCausesQuery } from '@/graphs/gql';

type AddOnType = 'Donation' | 'Sweepstakes' | 'Glow' | 'TicketTier';

interface CombinedAddon {
  source: 'addon' | 'varient';
  index: number;
  type: AddOnType;
  data: any;
}

const { makeGraphRequest, getAuthToken } = globalShared();

const item = computed({
  get: () => store.getters['sticker/GET_STICKER'],
  set: (value) => {
    store.dispatch('sticker/SET_STICKER', value);
  },
});

const showTypeDialog = ref(false);

const sweepstakesStickers = ref<any[]>([]);
const loadingSweepstakes = ref(false);

const glows = ref<any[]>([]);
const loadingGlows = ref(false);

const orgId = computed(() => item.value.organization?.id
  || store.getters['organizations/GET_SELECTED_ORGANIZATION']?.id);

const fetchGlows = async () => {
  const id = orgId.value;
  if (!id) return;
  loadingGlows.value = true;
  try {
    const response = await makeGraphRequest(
      'query',
      findGlows,
      { orgId: id, take: 200 },
      getAuthToken(),
    );
    glows.value = response?.data?.getGlows || [];
  } catch (error) {
    console.error('[TabAddOns] fetchGlows error:', error);
  } finally {
    loadingGlows.value = false;
  }
};

const openCreateGlow = () => {
  const width = 1100;
  const height = 780;
  const left = Math.round((window.screen.width - width) / 2);
  const top = Math.round((window.screen.height - height) / 2);
  window.open(
    '/manage-content/media/glows/create',
    'CreateGlowPopup',
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`,
  );
};

const sweepstakesSearch = ref('');

const filteredSweepstakes = computed(() => {
  if (!sweepstakesSearch.value.trim()) return sweepstakesStickers.value;
  const q = sweepstakesSearch.value.toLowerCase();
  return sweepstakesStickers.value.filter((s: any) => s.title?.toLowerCase().includes(q));
});

const getSelectedSweepstakes = (addon: CombinedAddon): any => {
  if (!addon.data.sweepstakesStickerId) return null;
  return sweepstakesStickers.value.find((s: any) => s.id === addon.data.sweepstakesStickerId) || null;
};

const formatSweepstakesDateRange = (s: any): string => {
  const start = s.startDate ? dayjs(s.startDate).format('MMM D') : null;
  const end = s.endDate ? dayjs(s.endDate).format('MMM D, YYYY') : null;
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  return '';
};

const glowSearch = ref('');

const filteredGlows = computed(() => {
  if (!glowSearch.value.trim()) return glows.value;
  const q = glowSearch.value.toLowerCase();
  return glows.value.filter((g: any) => g.title?.toLowerCase().includes(q));
});

const getSelectedGlow = (addon: CombinedAddon): any => {
  if (!addon.data.glowId) return null;
  return glows.value.find((g: any) => g.id === addon.data.glowId) || null;
};

const formatGlowDateRange = (glow: any): string => {
  const start = glow.glowStartDate ? dayjs(glow.glowStartDate).format('MMM D') : null;
  const end = glow.glowEndDate ? dayjs(glow.glowEndDate).format('MMM D, YYYY') : null;
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  return '';
};

const fetchSweepstakesStickers = async () => {
  const id = orgId.value;
  if (!id) {
    console.warn('[TabAddOns] Cannot fetch sweepstakes: orgId not available');
    return;
  }
  loadingSweepstakes.value = true;
  try {
    const response = await makeGraphRequest(
      'query',
      findApprovedOrgStickers,
      { orgId: id },
      getAuthToken(),
    );
    const all = response?.data?.findApprovedOrgStickers || [];
    sweepstakesStickers.value = all.filter((s: any) => s.campaignType === 'Sweepstakes');
    console.log('[TabAddOns] Loaded sweepstakes stickers:', sweepstakesStickers.value.length);
  } catch (error) {
    console.error('[TabAddOns] fetchSweepstakesStickers error:', error);
  } finally {
    loadingSweepstakes.value = false;
  }
};

onMounted(() => {
  fetchSweepstakesStickers();
  fetchGlows();
});

watch(orgId, (newOrgId, oldOrgId) => {
  if (newOrgId && newOrgId !== oldOrgId) {
    fetchSweepstakesStickers();
    fetchGlows();
  }
});

const combinedAddOns = computed((): CombinedAddon[] => {
  const result: CombinedAddon[] = [];
  const addOns: any[] = item.value.addOns || [];
  const varients: any[] = item.value.varients || [];

  addOns.forEach((addon, index) => {
    result.push({ source: 'addon', index, type: addon.type as AddOnType, data: addon });
  });

  varients.forEach((v, index) => {
    if (v.isAddOn) {
      result.push({ source: 'varient', index, type: 'TicketTier', data: v });
    }
  });

  result.sort((a, b) => (a.data.sortOrder ?? 0) - (b.data.sortOrder ?? 0));
  return result;
});

const typeOptions: { type: AddOnType; label: string; icon: string }[] = [
  { type: 'Donation', label: 'Donation', icon: 'dollar-sign' },
  { type: 'Sweepstakes', label: 'Sweepstakes', icon: 'gift' },
  { type: 'Glow', label: 'Glow', icon: 'sparkles' },
  { type: 'TicketTier', label: 'Ticket Tier', icon: 'ticket' },
];

const donationPricingOptions = [
  { label: 'Fixed Amount', value: 'fixed' },
  { label: 'Suggested Amounts (with custom option)', value: 'suggested' },
  { label: 'Custom (attendee chooses)', value: 'custom' },
];

const headerConfig: Record<AddOnType, { icon: string; label: string; badgeMod: string; iconMod: string }> = {
  Donation: { icon: 'dollar-sign', label: 'Donation Tier', badgeMod: 'teal', iconMod: 'teal' },
  Sweepstakes: { icon: 'gift', label: 'Sweepstakes Tier', badgeMod: 'purple', iconMod: 'purple' },
  Glow: { icon: 'sparkles', label: 'Glow Tier', badgeMod: 'yellow', iconMod: 'yellow' },
  TicketTier: { icon: 'ticket', label: 'Additional Ticket Tier', badgeMod: 'blue', iconMod: 'blue' },
};

const onSelectType = (type: AddOnType) => {
  const totalAddOns = (item.value.addOns || []).length
    + (item.value.varients || []).filter((v: any) => v.isAddOn).length;
  const sortOrder = totalAddOns;

  if (type === 'TicketTier') {
    const varients = [...(item.value.varients || [])];
    varients.push({
      id: null,
      title: '',
      cost: 0,
      isFree: false,
      whatsIncluded: null,
      selectionOptions: [],
      isAddOn: true,
      quantity: null,
      sortOrder,
      stickerId: null,
      description: null,
      isActive: true,
    });
    store.dispatch('sticker/SET_STICKER', { varients });
  } else {
    const addOns = [...(item.value.addOns || [])];
    const newAddOn: any = { id: null, type, sortOrder };

    if (type === 'Donation') {
      newAddOn.donationCauses = [];
      newAddOn.donationPricing = 'suggested';
      newAddOn.donationFixed = null;
      newAddOn.donationSuggested = [5, 10, 25, 50];
    } else if (type === 'Sweepstakes') {
      newAddOn.sweepstakesStickerId = null;
    } else if (type === 'Glow') {
      newAddOn.glowIsExisting = false;
      newAddOn.glowId = null;
      newAddOn.glowName = null;
      newAddOn.glowDescription = null;
      newAddOn.glowIsFree = true;
      newAddOn.glowPrice = null;
    }

    addOns.push(newAddOn);
    store.dispatch('sticker/SET_STICKER', { addOns });
  }

  showTypeDialog.value = false;
};

const onRemoveAddon = (addon: CombinedAddon) => {
  if (addon.source === 'addon') {
    const addOns = [...(item.value.addOns || [])];
    addOns.splice(addon.index, 1);
    store.dispatch('sticker/SET_STICKER', { addOns });
  } else {
    const varients = [...(item.value.varients || [])];
    varients.splice(addon.index, 1);
    store.dispatch('sticker/SET_STICKER', { varients });
  }
};

const updateField = (addon: CombinedAddon, field: string, value: any) => {
  if (addon.source === 'addon') {
    const addOns = [...(item.value.addOns || [])];
    addOns[addon.index] = { ...addOns[addon.index], [field]: value };
    store.dispatch('sticker/SET_STICKER', { addOns });
  } else {
    const varients = [...(item.value.varients || [])];
    varients[addon.index] = { ...varients[addon.index], [field]: value };
    store.dispatch('sticker/SET_STICKER', { varients });
  }
};


const updateSuggestedAmount = (addon: CombinedAddon, amtIndex: number, value: number) => {
  const amounts = [...(addon.data.donationSuggested || [])];
  amounts[amtIndex] = value;
  updateField(addon, 'donationSuggested', amounts);
};

const preventArrowKeys = (event: KeyboardEvent) => {
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') event.preventDefault();
};

// ── Charity (Benevity) search ────────────────────────────────────────────────
interface BenevityCause {
  id: string;
  name: string;
  logo?: string;
}

const charitySearchInputs = ref<{ [addonKey: string]: string }>({});
const charitySearchResults = ref<{ [addonKey: string]: BenevityCause[] }>({});
const charitySearchLoading = ref<{ [addonKey: string]: boolean }>({});
const charityDropdownOpen = ref<{ [addonKey: string]: boolean }>({});

const addonKey = (addon: CombinedAddon) => `${addon.source}-${addon.index}`;

let charitySearchTimers: { [key: string]: ReturnType<typeof setTimeout> } = {};

async function searchCharities(addon: CombinedAddon, query: string) {
  const key = addonKey(addon);
  charitySearchInputs.value[key] = query;

  clearTimeout(charitySearchTimers[key]);

  if (!query || query.trim().length < 2) {
    charitySearchResults.value[key] = [];
    charityDropdownOpen.value[key] = false;
    return;
  }

  charitySearchTimers[key] = setTimeout(async () => {
    charitySearchLoading.value[key] = true;
    try {
      const response = await makeGraphRequest(
        'query',
        benevityCausesQuery,
        { q: query.trim(), page: '1' },
        getAuthToken(),
      );
      charitySearchResults.value[key] = response?.data?.benevityCauses?.causes || [];
      charityDropdownOpen.value[key] = charitySearchResults.value[key].length > 0;
    } catch (err) {
      console.error('[TabAddOns] charity search error:', err);
      charitySearchResults.value[key] = [];
    } finally {
      charitySearchLoading.value[key] = false;
    }
  }, 350);
}

function selectCharity(addon: CombinedAddon, cause: BenevityCause) {
  const key = addonKey(addon);
  const causeString = `${cause.name} : ${cause.id}`;
  const existing: string[] = addon.data.donationCauses || [];
  if (!existing.includes(causeString)) {
    updateField(addon, 'donationCauses', [...existing.filter((c: string) => c.trim()), causeString]);
  }
  charitySearchInputs.value[key] = '';
  charitySearchResults.value[key] = [];
  charityDropdownOpen.value[key] = false;
}

function removeCharity(addon: CombinedAddon, causeString: string) {
  const existing: string[] = addon.data.donationCauses || [];
  updateField(addon, 'donationCauses', existing.filter((c: string) => c !== causeString));
}

function closeCharityDropdown(addon: CombinedAddon) {
  const key = addonKey(addon);
  setTimeout(() => {
    charityDropdownOpen.value[key] = false;
  }, 200);
}

function getCauseName(causeStr: string) {
  return causeStr.includes(' : ') ? causeStr.split(' : ')[0] : causeStr;
}

function getCauseId(causeStr: string) {
  return causeStr.includes(' : ') ? causeStr.split(' : ')[1] : '';
}

const validDonationCauses = (addon: CombinedAddon): string[] => (addon.data.donationCauses || [])
  .filter((c: string) => c && c.trim().length > 0);

// Bullet point helpers for TicketTier add-ons
const getBulletLines = (whatsIncluded: string | null): string[] => {
  if (whatsIncluded === null || whatsIncluded === undefined) return [];
  return whatsIncluded.split('\n');
};

const onAddBullet = (addon: CombinedAddon) => {
  const lines = addon.data.whatsIncluded ? addon.data.whatsIncluded.split('\n') : [];
  lines.push('');
  updateField(addon, 'whatsIncluded', lines.join('\n'));
};

const updateBulletLine = (addon: CombinedAddon, lineIndex: number, value: string) => {
  const lines = addon.data.whatsIncluded ? addon.data.whatsIncluded.split('\n') : [];
  lines[lineIndex] = value;
  updateField(addon, 'whatsIncluded', lines.join('\n'));
};

const removeBulletLine = (addon: CombinedAddon, lineIndex: number) => {
  const lines = addon.data.whatsIncluded ? addon.data.whatsIncluded.split('\n') : [];
  lines.splice(lineIndex, 1);
  updateField(addon, 'whatsIncluded', lines.join('\n'));
};

// Selection options helpers for TicketTier add-ons
const onAddOption = (addon: CombinedAddon) => {
  const options = Array.isArray(addon.data.selectionOptions) ? [...addon.data.selectionOptions] : [];
  options.push('');
  updateField(addon, 'selectionOptions', options);
};

const onRemoveOption = (addon: CombinedAddon, optIndex: number) => {
  const options = Array.isArray(addon.data.selectionOptions) ? [...addon.data.selectionOptions] : [];
  options.splice(optIndex, 1);
  updateField(addon, 'selectionOptions', options);
};

const updateOption = (addon: CombinedAddon, optIndex: number, value: string) => {
  const options = Array.isArray(addon.data.selectionOptions) ? [...addon.data.selectionOptions] : [];
  options[optIndex] = value;
  updateField(addon, 'selectionOptions', options);
};
</script>

<template lang="pug">
section.c-tab-addons

  .c-addon-card(
    v-for="(addon, idx) in combinedAddOns"
    :key="`${addon.source}-${addon.index}`"
  )
    .c-addon-card__header
      .c-addon-card__header-left
        .c-addon-card__icon-wrap(:class="`c-addon-card__icon-wrap--${headerConfig[addon.type].iconMod}`")
          fai(type="fal" :icon="headerConfig[addon.type].icon")
        span.c-addon-card__title {{ headerConfig[addon.type].label }}
        span.c-addon-card__badge(:class="`c-addon-card__badge--${headerConfig[addon.type].badgeMod}`") Add-On

    .c-addon-card__body

      //- Donation
      template(v-if="addon.type === 'Donation'")
        .c-addon-card__field
          label.c-addon-card__label
            | Charity Beneficiary(ies)
            span.c-addon-card__required *

          //- Selected charity chips
          .c-charity-chips(v-if="validDonationCauses(addon).length")
            .c-charity-chip(
              v-for="causeStr in validDonationCauses(addon)"
              :key="causeStr"
            )
              .c-charity-chip__info
                span.c-charity-chip__name {{ getCauseName(causeStr) }}
                span.c-charity-chip__ein(v-if="getCauseId(causeStr)") EIN: {{ getCauseId(causeStr) }}
              button.c-charity-chip__remove(
                type="button"
                @click="removeCharity(addon, causeStr)"
              ) ×

          //- Search input + dropdown
          .c-charity-search
            .c-charity-search__input-wrap
              input.c-input.c-charity-search__input(
                type="text"
                placeholder="Search for a non-profit charity..."
                :value="charitySearchInputs[addonKey(addon)] || ''"
                @input="(e) => searchCharities(addon, e.target.value)"
                @blur="closeCharityDropdown(addon)"
              )
              .c-charity-search__spinner(v-if="charitySearchLoading[addonKey(addon)]")
                fai(type="fal" icon="sync").fa-spin.c-icon-sm

            .c-charity-search__dropdown(v-if="charityDropdownOpen[addonKey(addon)] && charitySearchResults[addonKey(addon)]?.length")
              button.c-charity-result(
                v-for="result in charitySearchResults[addonKey(addon)]"
                :key="result.id"
                type="button"
                @mousedown.prevent="selectCharity(addon, result)"
              )
                img.c-charity-result__logo(
                  v-if="result.logo"
                  :src="result.logo"
                  :alt="result.name"
                )
                .c-charity-result__logo-placeholder(v-else)
                  fai(type="fal" icon="hand-holding-heart")
                .c-charity-result__body
                  span.c-charity-result__name {{ result.name }}
                  span.c-charity-result__id EIN: {{ result.id }}

          p.c-addon-card__hint.c-addon-card__hint--warning(
            v-if="validDonationCauses(addon).length === 0"
          ) At least one charity is required for donation add-ons.

        .c-addon-card__field
          label.c-addon-card__label Pricing Type
          el-select.c-addon-card__select(
            :model-value="addon.data.donationPricing"
            @update:model-value="(val) => updateField(addon, 'donationPricing', val)"
          )
            el-option(
              v-for="opt in donationPricingOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            )

        template(v-if="addon.data.donationPricing === 'suggested'")
          .c-addon-card__field
            label.c-addon-card__label Suggested Amounts
            p.c-addon-card__hint Users can also enter a custom amount if they choose
            .c-addon-card__amounts
              .c-addon-card__amount-box(
                v-for="(amt, ai) in (addon.data.donationSuggested || [])"
                :key="ai"
              )
                span.c-addon-card__amount-prefix $
                input.c-input.c-input--amount(
                  type="number"
                  :value="amt"
                  min="0"
                  @input="(e) => updateSuggestedAmount(addon, ai, parseFloat(e.target.value) || 0)"
                  @keydown="preventArrowKeys"
                  @wheel.prevent
                )

        template(v-if="addon.data.donationPricing === 'fixed'")
          .c-addon-card__field
            label.c-addon-card__label Fixed Amount
            .c-addon-card__cost-input
              span.c-addon-card__cost-prefix $
              input.c-input.c-input--cost(
                type="number"
                placeholder="0.00"
                min="0"
                step="0.01"
                :value="addon.data.donationFixed"
                @input="(e) => updateField(addon, 'donationFixed', parseFloat(e.target.value) || 0)"
                @keydown="preventArrowKeys"
                @wheel.prevent
              )

      //- Sweepstakes
      template(v-if="addon.type === 'Sweepstakes'")
        .c-addon-card__field
          .c-addon-card__sweep-header
            label.c-addon-card__label Linked Sweepstakes
            span.c-addon-card__sweep-counter(v-if="sweepstakesStickers.length > 0") {{ sweepstakesStickers.length }} available

          //- Selected sweepstakes card
          .c-sweep-selected(v-if="getSelectedSweepstakes(addon)")
            .c-sweep-selected__accent
            .c-sweep-selected__thumb(v-if="getSelectedSweepstakes(addon).bannerUrl")
              img(:src="getSelectedSweepstakes(addon).bannerUrl" alt="")
            .c-sweep-selected__thumb.c-sweep-selected__thumb--placeholder(v-else)
              fai(type="fal" icon="gift")
            .c-sweep-selected__info
              .c-sweep-selected__top
                span.c-sweep-selected__title {{ getSelectedSweepstakes(addon).title }}
                span.c-sweep-selected__type Sweepstakes
              .c-sweep-selected__meta
                span.c-sweep-selected__dates(v-if="formatSweepstakesDateRange(getSelectedSweepstakes(addon))")
                  fai(type="fal" icon="calendar-alt").c-sweep-selected__dates-icon
                  | {{ formatSweepstakesDateRange(getSelectedSweepstakes(addon)) }}
                span.c-sweep-selected__status(
                  v-if="getSelectedSweepstakes(addon).status"
                  :class="`c-sweep-selected__status--${getSelectedSweepstakes(addon).status.toLowerCase()}`"
                ) {{ getSelectedSweepstakes(addon).status }}
            button.c-sweep-selected__clear(type="button" @click="updateField(addon, 'sweepstakesStickerId', null)")
              fai(type="fal" icon="times")

          //- Sweepstakes list picker
          template(v-else)
            .c-sweep-picker(v-if="!loadingSweepstakes && sweepstakesStickers.length > 0")
              input.c-sweep-picker__search(
                v-model="sweepstakesSearch"
                type="text"
                placeholder="Search sweepstakes..."
              )
              .c-sweep-picker__list
                button.c-sweep-item(
                  v-for="s in filteredSweepstakes"
                  :key="s.id"
                  type="button"
                  @click="updateField(addon, 'sweepstakesStickerId', s.id)"
                )
                  .c-sweep-item__thumb(v-if="s.bannerUrl")
                    img(:src="s.bannerUrl" alt="")
                  .c-sweep-item__thumb.c-sweep-item__thumb--placeholder(v-else)
                    fai(type="fal" icon="gift")
                  .c-sweep-item__body
                    span.c-sweep-item__title {{ s.title }}
                    span.c-sweep-item__dates(v-if="formatSweepstakesDateRange(s)")
                      fai(type="fal" icon="calendar-alt").c-sweep-item__icon
                      | {{ formatSweepstakesDateRange(s) }}
                  span.c-sweep-item__status(
                    v-if="s.status"
                    :class="`c-sweep-item__status--${s.status.toLowerCase()}`"
                  ) {{ s.status }}
              p.c-addon-card__hint(v-if="filteredSweepstakes.length === 0") No sweepstakes match your search.
            .c-sweep-picker__loading(v-else-if="loadingSweepstakes")
              fai(type="fal" icon="sync").fa-spin.c-icon-sm
              span  Loading sweepstakes...
            .c-sweep-picker__empty(v-else)
              fai(type="fal" icon="gift").c-sweep-picker__empty-icon
              p.c-sweep-picker__empty-text No sweepstakes stickers found
              p.c-addon-card__hint Create a Sweepstakes sticker first, then come back to link it here.

      //- Glow
      template(v-if="addon.type === 'Glow'")
        .c-addon-card__field
          .c-addon-card__glow-select-header
            label.c-addon-card__label Select Glow
            .c-addon-card__glow-select-actions
              button.c-addon-card__refresh-btn(
                type="button"
                :disabled="loadingGlows"
                @click="fetchGlows"
              )
                fai(type="fal" icon="sync" :class="{ 'fa-spin': loadingGlows }").c-icon-sm
                span Refresh
              button.c-addon-card__open-glow-btn(type="button" @click="openCreateGlow")
                fai(type="fal" icon="external-link-alt").c-icon-sm
                span Open Glow Builder

          //- Selected glow card
          .c-glow-selected(v-if="getSelectedGlow(addon)")
            .c-glow-selected__color-bar(:style="{ background: getSelectedGlow(addon).glowColor || '#F8E587' }")
            .c-glow-selected__info
              span.c-glow-selected__title {{ getSelectedGlow(addon).title }}
              span.c-glow-selected__dates(v-if="formatGlowDateRange(getSelectedGlow(addon))")
                fai(type="fal" icon="calendar-alt").c-glow-selected__dates-icon
                | {{ formatGlowDateRange(getSelectedGlow(addon)) }}
              .c-glow-selected__meta
                span.c-glow-selected__status(
                  v-if="getSelectedGlow(addon).status"
                  :class="`c-glow-selected__status--${getSelectedGlow(addon).status.toLowerCase()}`"
                ) {{ getSelectedGlow(addon).status }}
                span.c-glow-selected__location(v-if="getSelectedGlow(addon).geolocation")
                  fai(type="fal" icon="map-marker-alt").c-glow-selected__location-icon
                  | {{ getSelectedGlow(addon).geolocation }}
            button.c-glow-selected__clear(type="button" @click="updateField(addon, 'glowId', null)")
              fai(type="fal" icon="times")

          //- Loading state — shown regardless of whether a glow is selected
          .c-glow-picker__loading(v-if="loadingGlows")
            fai(type="fal" icon="sync").fa-spin.c-icon-sm
            span  Loading glows...

          //- Glow list picker
          template(v-else-if="!getSelectedGlow(addon)")
            .c-glow-picker(v-if="glows.length > 0")
              input.c-glow-picker__search(
                v-model="glowSearch"
                type="text"
                placeholder="Search glows..."
              )
              .c-glow-picker__list
                button.c-glow-item(
                  v-for="g in filteredGlows"
                  :key="g.id"
                  type="button"
                  @click="updateField(addon, 'glowId', g.id)"
                )
                  .c-glow-item__color(:style="{ background: g.glowColor || '#F8E587' }")
                  .c-glow-item__body
                    span.c-glow-item__title {{ g.title }}
                    span.c-glow-item__dates(v-if="formatGlowDateRange(g)")
                      fai(type="fal" icon="calendar-alt").c-glow-item__icon
                      | {{ formatGlowDateRange(g) }}
                  span.c-glow-item__status(
                    v-if="g.status"
                    :class="`c-glow-item__status--${g.status.toLowerCase()}`"
                  ) {{ g.status }}
              p.c-addon-card__hint(v-if="filteredGlows.length === 0")
                | No glows match your search.
            p.c-addon-card__hint(v-else)
              | No glows found.&nbsp;
              a.c-addon-card__hint-link(@click="openCreateGlow") Open Glow Builder
              | &nbsp;to create one, then click Refresh.
        
        //- Glow pricing (for both new and existing)
        .c-addon-card__row
          .c-addon-card__field
            label.c-addon-card__label Pricing
            el-select.c-addon-card__select(
              :model-value="addon.data.glowIsFree ? 'Free' : 'Paid'"
              @update:model-value="(val) => updateField(addon, 'glowIsFree', val === 'Free')"
            )
              el-option(label="Free" value="Free")
              el-option(label="Paid" value="Paid")
          .c-addon-card__field(v-if="!addon.data.glowIsFree")
            label.c-addon-card__label Price
            .c-addon-card__cost-input
              span.c-addon-card__cost-prefix $
              input.c-input.c-input--cost(
                type="number"
                placeholder="0.00"
                min="0"
                step="0.01"
                :value="addon.data.glowPrice"
                @input="(e) => updateField(addon, 'glowPrice', parseFloat(e.target.value) || 0)"
                @keydown="preventArrowKeys"
                @wheel.prevent
              )

      //- Ticket Tier (isAddOn varient)
      template(v-if="addon.type === 'TicketTier'")
        .c-addon-card__row
          .c-addon-card__field
            label.c-addon-card__label Tier Name
            input.c-input(
              type="text"
              placeholder="e.g., Backstage Pass"
              :value="addon.data.title"
              @input="(e) => updateField(addon, 'title', e.target.value)"
            )
          .c-addon-card__field
            label.c-addon-card__label Type
            el-select.c-addon-card__select(
              :model-value="addon.data.isFree ? 'Free' : 'Paid'"
              @update:model-value="(val) => updateField(addon, 'isFree', val === 'Free')"
            )
              el-option(label="Paid" value="Paid")
              el-option(label="Free" value="Free")

        .c-addon-card__field
          label.c-addon-card__label Tier Description
          textarea.c-input.c-input--textarea(
            placeholder="Details for this ticket tier"
            :value="addon.data.description"
            @input="(e) => updateField(addon, 'description', e.target.value)"
          )

        .c-addon-card__row.c-addon-card__row--sections
          .c-addon-card__section
            .c-addon-card__section-header
              label.c-addon-card__label What's Included
              button.c-addon-card__add-link(type="button" @click="onAddBullet(addon)") + Add Bullet
            p.c-addon-card__hint Add bullet points to highlight what's included in this tier
            .c-addon-card__bullets(v-if="getBulletLines(addon.data.whatsIncluded).length")
              .c-addon-card__bullet-row(
                v-for="(line, li) in getBulletLines(addon.data.whatsIncluded)"
                :key="li"
              )
                input.c-input(
                  type="text"
                  placeholder="Bullet point..."
                  :value="line"
                  @input="(e) => updateBulletLine(addon, li, e.target.value)"
                )
                button.c-addon-card__bullet-remove(
                  type="button"
                  @click="removeBulletLine(addon, li)"
                ) ×

          .c-addon-card__section
            .c-addon-card__section-header
              label.c-addon-card__label Selection Options (optional)
              button.c-addon-card__add-link(type="button" @click="onAddOption(addon)") + Add Option
            p.c-addon-card__hint Add choices like meal preferences, t-shirt sizes, etc.
            .c-addon-card__options(v-if="Array.isArray(addon.data.selectionOptions) && addon.data.selectionOptions.length")
              .c-addon-card__option-row(
                v-for="(opt, oi) in addon.data.selectionOptions"
                :key="oi"
              )
                input.c-input(
                  type="text"
                  placeholder="Option..."
                  :value="opt"
                  @input="(e) => updateOption(addon, oi, e.target.value)"
                )
                button.c-addon-card__option-remove(
                  type="button"
                  @click="onRemoveOption(addon, oi)"
                ) ×

        .c-addon-card__row.c-addon-card__row--bottom
          .c-addon-card__field
            label.c-addon-card__label Max Quantity (optional)
            input.c-input(
              type="number"
              placeholder="100"
              min="0"
              :value="addon.data.quantity"
              @input="(e) => updateField(addon, 'quantity', e.target.value ? parseInt(e.target.value) : null)"
              @keydown="preventArrowKeys"
              @wheel.prevent
            )
          .c-addon-card__field(v-if="!addon.data.isFree")
            label.c-addon-card__label Cost
            .c-addon-card__cost-input
              span.c-addon-card__cost-prefix $
              input.c-input.c-input--cost(
                type="number"
                placeholder="0.00"
                min="0"
                step="0.01"
                :value="addon.data.cost"
                @input="(e) => updateField(addon, 'cost', parseFloat(e.target.value) || 0)"
                @keydown="preventArrowKeys"
                @wheel.prevent
              )

    .c-addon-card__footer
      button.c-addon-card__remove-btn(type="button" @click="onRemoveAddon(addon)") Remove Tier

  //- Add button
  button.c-addons__add(type="button" @click="showTypeDialog = true")
    fai(type="fal" icon="plus-circle").size-16
    span + Add Add-On Tier

  //- Type selection dialog
  el-dialog(
    v-model="showTypeDialog"
    title="Select Add-On Type"
    width="500px"
    :close-on-click-modal="true"
    :show-close="true"
  )
    p.c-type-dialog__subtitle Choose the type of add-on tier to create
    .c-type-dialog__grid
      button.c-type-dialog__card(
        v-for="opt in typeOptions"
        :key="opt.type"
        type="button"
        @click="onSelectType(opt.type)"
      )
        .c-type-dialog__card-icon-wrap
          fai.c-type-dialog__card-icon(type="fal" :icon="opt.icon")
        span.c-type-dialog__card-label {{ opt.label }}
</template>

<style scoped lang="scss">
// Utility: constrain any fai SVG to a small inline size
.c-icon-sm {
  font-size: 13px;
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

.c-tab-addons {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// ── Add button ──────────────────────────────────────────────────────
.c-addons__add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  background: none;
  border: 1px dashed #ccc;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  font-family: 'Red Hat Display', sans-serif;
  transition: border-color 0.2s, color 0.2s;

  &:hover {
    border-color: #1ebeaf;
    color: #1ebeaf;
  }
}

// ── Addon card ──────────────────────────────────────────────────────
.c-addon-card {
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  background: #fff;
  overflow: visible;

  &__header {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    border-bottom: 1px solid #f0f0f0;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
  }

  &__icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    flex-shrink: 0;

    &--teal   { background: #e6f9f7; color: #1ebeaf; }
    &--purple { background: #ede9fe; color: #7c3aed; }
    &--yellow { background: #fef3c7; color: #d97706; }
    &--blue   { background: #dbeafe; color: #2563eb; }
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    color: #111;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    letter-spacing: 0.01em;

    &--teal   { background: #d1faf6; color: #0d8f87; }
    &--purple { background: #ede9fe; color: #6d28d9; }
    &--yellow { background: #fef3c7; color: #b45309; }
    &--blue   { background: #dbeafe; color: #1d4ed8; }
  }

  &__body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__required {
    color: #e02020;
    margin-left: 2px;
    font-weight: 700;
  }

  &__hint {
    font-size: 12px;
    color: #888;
    margin: 0;
    font-family: 'Red Hat Display', sans-serif;
    line-height: 1.4;

    &--warning {
      color: #d97706;
      font-weight: 500;
    }
  }

  // Section for bullet points
  &__section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-right: 26px;
  }

  &__add-link {
    background: none;
    border: 1px solid #1ebeaf;
    color: #1ebeaf;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 5px;
    font-family: 'Red Hat Display', sans-serif;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: #1ebeaf;
      color: #fff;
    }
  }

  &__bullets {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__bullet-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .c-input {
      flex: 1;
    }
  }

  &__bullet-remove {
    background: none;
    border: none;
    font-size: 18px;
    color: #aaa;
    cursor: pointer;
    line-height: 1;
    padding: 0 4px;
    flex-shrink: 0;
    transition: color 0.2s;

    &:hover {
      color: #e02020;
    }
  }

  // Selection options
  &__options {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__option-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .c-input {
      flex: 1;
    }
  }

  &__option-remove {
    background: none;
    border: none;
    font-size: 18px;
    color: #aaa;
    cursor: pointer;
    line-height: 1;
    padding: 0 4px;
    flex-shrink: 0;
    transition: color 0.2s;

    &:hover {
      color: #e02020;
    }
  }

  &__select {
    width: 100%;

    :deep(.el-select__wrapper),
    :deep(.el-input__wrapper) {
      height: 48px !important;
      box-sizing: border-box;
      padding: 0 16px;
    }
  }

  // Charity search (replaces old cause text inputs)
  &__required {
    color: #e02020;
    margin-left: 3px;
    font-weight: 700;
  }

  // Suggested amounts
  &__amounts {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 4px;
  }

  &__amount-box {
    display: flex;
    align-items: center;
    border: 1px solid #ddd;
    border-radius: 6px;
    overflow: hidden;
    background: #fafafa;
    min-width: 90px;
  }

  &__amount-prefix {
    padding: 0 8px;
    font-size: 14px;
    font-weight: 600;
    color: #555;
    border-right: 1px solid #e8e8e8;
    background: #f5f5f5;
    height: 48px;
    display: flex;
    align-items: center;
  }

  // Fixed / ticket tier cost
  &__cost-input {
    display: flex;
    align-items: center;
    border: 1px solid #ccc;
    border-radius: 5px;
    overflow: hidden;
    background: #fff;
  }

  &__cost-prefix {
    padding: 0 12px;
    font-size: 14px;
    color: #555;
    border-right: 1px solid #ddd;
    background: #f5f5f5;
    height: 48px;
    display: flex;
    align-items: center;
  }

  // Glow select header (label + action buttons)
  &__glow-select-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
  }

  &__glow-select-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__open-glow-btn {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    gap: 5px;
    padding: 4px 12px;
    background: #1ebeaf;
    border: none;
    border-radius: 5px;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Red Hat Display', sans-serif;
    cursor: pointer;
    transition: background 0.2s;

    svg, .fai {
      font-size: 10px;
      width: 10px;
      height: 10px;
    }

    &:hover {
      background: #17a99b;
    }
  }

  &__refresh-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: none;
    border: 1px solid #ddd;
    border-radius: 5px;
    color: #555;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Red Hat Display', sans-serif;
    padding: 4px 10px;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;

    &:hover:not(:disabled) {
      border-color: #1ebeaf;
      color: #1ebeaf;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__hint-link {
    color: #1ebeaf;
    cursor: pointer;
    text-decoration: underline;
    font-weight: 600;
  }
}

// ── Sweepstakes header ───────────────────────────────────────────────
.c-addon-card {
  &__sweep-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
  }

  &__sweep-counter {
    font-size: 11px;
    font-weight: 600;
    color: #7c3aed;
    background: #ede9fe;
    padding: 2px 8px;
    border-radius: 20px;
    font-family: 'Red Hat Display', sans-serif;
  }
}

// ── Sweepstakes selected card ─────────────────────────────────────────
.c-sweep-selected {
  display: flex;
  align-items: stretch;
  border: 1.5px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  margin-top: 4px;

  &__accent {
    width: 5px;
    flex-shrink: 0;
    background: #7c3aed;
  }

  &__thumb {
    width: 56px;
    flex-shrink: 0;
    overflow: hidden;
    background: #f3f0ff;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    &--placeholder {
      color: #7c3aed;

      svg {
        font-size: 20px;
        width: 20px;
        height: 20px;
      }
    }
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px 12px;
    min-width: 0;
  }

  &__top {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: #111;
    font-family: 'Red Hat Display', sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__type {
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 8px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background: #ede9fe;
    color: #6d28d9;
    flex-shrink: 0;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: #666;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__dates-icon {
    font-size: 11px;
    width: 11px;
    height: 11px;
    flex-shrink: 0;
    color: #999;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 8px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &--active, &--approved  { background: #d1faf6; color: #0d8f87; }
    &--pending              { background: #fef3c7; color: #b45309; }
    &--inactive, &--expired { background: #f3f4f6; color: #6b7280; }
  }

  &__clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    flex-shrink: 0;
    background: none;
    border: none;
    border-left: 1px solid #f0f0f0;
    color: #aaa;
    cursor: pointer;
    font-size: 14px;
    transition: color 0.2s, background 0.2s;

    &:hover {
      color: #e02020;
      background: #fff5f5;
    }
  }
}

// ── Sweepstakes picker list ───────────────────────────────────────────
.c-sweep-picker {
  margin-top: 4px;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;

  &__search {
    display: block;
    width: 100%;
    box-sizing: border-box;
    border: none;
    border-bottom: 1px solid #f0f0f0;
    padding: 9px 14px;
    font-size: 13px;
    font-family: 'Red Hat Display', sans-serif;
    color: #222;
    background: #fafafa;
    outline: none;

    &::placeholder { color: #bbb; }
    &:focus { background: #fff; }
  }

  &__list {
    max-height: 220px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    font-size: 13px;
    color: #888;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 24px 16px;
    text-align: center;
  }

  &__empty-icon {
    font-size: 28px !important;
    width: 28px !important;
    height: 28px !important;
    max-width: 28px;
    max-height: 28px;
    color: #d8b4fe;
  }

  &__empty-text {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #555;
    font-family: 'Red Hat Display', sans-serif;
  }
}

.c-sweep-item {
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  background: #fff;
  border: none;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
  padding: 0;

  &:last-child { border-bottom: none; }
  &:hover { background: #faf5ff; }

  &__thumb {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    overflow: hidden;
    background: #f3f0ff;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    &--placeholder {
      color: #c4b5fd;

      svg {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
    }
  }

  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 9px 12px;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: #111;
    font-family: 'Red Hat Display', sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #888;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__icon {
    font-size: 10px;
    width: 10px;
    height: 10px;
    flex-shrink: 0;
    color: #bbb;
  }

  &__status {
    flex-shrink: 0;
    margin-right: 12px;
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 8px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &--active, &--approved  { background: #d1faf6; color: #0d8f87; }
    &--pending              { background: #fef3c7; color: #b45309; }
    &--inactive, &--expired { background: #f3f4f6; color: #6b7280; }
  }
}

// ── Glow selected card ──────────────────────────────────────────────
.c-glow-selected {
  display: flex;
  align-items: stretch;
  border: 1.5px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  margin-top: 4px;

  &__color-bar {
    width: 6px;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    min-width: 0;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: #111;
    font-family: 'Red Hat Display', sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: #666;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__dates-icon {
    font-size: 11px;
    width: 11px;
    height: 11px;
    flex-shrink: 0;
    color: #999;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 8px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &--active, &--approved  { background: #d1faf6; color: #0d8f87; }
    &--pending              { background: #fef3c7; color: #b45309; }
    &--inactive, &--expired { background: #f3f4f6; color: #6b7280; }
  }

  &__location {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #888;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__location-icon {
    font-size: 10px;
    color: #aaa;
  }

  &__clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    flex-shrink: 0;
    background: none;
    border: none;
    border-left: 1px solid #f0f0f0;
    color: #aaa;
    cursor: pointer;
    font-size: 14px;
    transition: color 0.2s, background 0.2s;

    &:hover {
      color: #e02020;
      background: #fff5f5;
    }
  }
}

// ── Glow picker list ────────────────────────────────────────────────
.c-glow-picker {
  margin-top: 4px;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;

  &__search {
    display: block;
    width: 100%;
    box-sizing: border-box;
    border: none;
    border-bottom: 1px solid #f0f0f0;
    padding: 9px 14px;
    font-size: 13px;
    font-family: 'Red Hat Display', sans-serif;
    color: #222;
    background: #fafafa;
    outline: none;

    &::placeholder { color: #bbb; }
    &:focus { background: #fff; }
  }

  &__list {
    max-height: 220px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    font-size: 13px;
    color: #888;
    font-family: 'Red Hat Display', sans-serif;
  }
}

.c-glow-item {
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  background: #fff;
  border: none;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
  padding: 0;

  &:last-child { border-bottom: none; }

  &:hover { background: #f7fdfc; }

  &__color {
    width: 5px;
    align-self: stretch;
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 9px 12px;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: #111;
    font-family: 'Red Hat Display', sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #888;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__icon {
    font-size: 10px;
    width: 10px;
    height: 10px;
    flex-shrink: 0;
    color: #bbb;
  }

  &__status {
    flex-shrink: 0;
    margin-right: 12px;
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 8px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &--active, &--approved  { background: #d1faf6; color: #0d8f87; }
    &--pending              { background: #fef3c7; color: #b45309; }
    &--inactive, &--expired { background: #f3f4f6; color: #6b7280; }
  }
}

.c-addon-card {
  // Two-column row (ticket tier)
  &__row {
    display: flex;
    gap: 16px;

    .c-addon-card__field { flex: 1; }

    &--bottom {
      align-items: flex-end;
    }

    &--sections {
      align-items: flex-start;
      
      .c-addon-card__section {
        flex: 1;
      }
    }
  }

  &__footer {
    border-top: 1px solid #f0f0f0;
    padding: 12px 20px;
    text-align: center;
  }

  &__remove-btn {
    background: none;
    border: none;
    color: #e02020;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Red Hat Display', sans-serif;
    padding: 0;
    transition: opacity 0.2s;

    &:hover { opacity: 0.75; }
  }

  // Shared input styles
  .c-input {
    width: 100%;
    height: 48px;
    min-height: 48px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 400;
    font-family: 'Red Hat Display', sans-serif;
    color: #222;
    outline: none;
    background: #fafafa;
    transition: border-color 0.2s, background 0.2s;
    box-sizing: border-box;

    &:focus {
      border-color: #1ebeaf;
      background: #fff;
    }

    &--error {
      border-color: #e02020 !important;
      background: #fff5f5;
    }

    &--textarea {
      height: 88px;
      min-height: 88px;
      padding: 12px 16px;
      resize: vertical;
    }

    &--amount {
      width: 64px;
      min-width: 64px;
      border: none;
      border-radius: 0;
      background: transparent;
      padding: 0 8px;
    }

    &--cost {
      border: none;
      border-radius: 0;
      flex: 1;
      background: transparent;
    }
  }

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
}

// ── Type selection dialog ───────────────────────────────────────────
.c-type-dialog {
  &__subtitle {
    margin: 0 0 20px;
    font-size: 13px;
    color: #888;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  &__card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 28px 16px;
    border: 1.5px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    font-family: 'Red Hat Display', sans-serif;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;

    &:hover {
      border-color: #1ebeaf;
      background: #f7fdfc;
      box-shadow: 0 2px 12px rgba(30, 190, 175, 0.1);
    }
  }

  &__card-icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: #e6f9f7;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1ebeaf;
    font-size: 22px;
  }

  &__card-icon {
    font-size: 22px;
    color: #1ebeaf;
    width: 22px;
    height: 22px;
  }

  &__card-label {
    font-size: 14px;
    font-weight: 700;
    color: #111;
  }
}

// Override el-dialog internals for the type dialog
:deep(.el-dialog__body) {
  padding-top: 8px;
}

// ── Charity chips (selected charities) ────────────────────────────────────────
.c-charity-chips {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

.c-charity-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #e6f9f7;
  border: 1px solid #b2f0ea;
  border-radius: 8px;
  gap: 8px;

  &__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: #0d6f68;
    font-family: 'Red Hat Display', sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__ein {
    font-size: 11px;
    color: #4a9b95;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__remove {
    background: none;
    border: none;
    font-size: 16px;
    color: #999;
    cursor: pointer;
    flex-shrink: 0;
    padding: 0 4px;
    line-height: 1;
    transition: color 0.2s;

    &:hover { color: #e02020; }
  }
}

// ── Charity search input + dropdown ──────────────────────────────────────────
.c-charity-search {
  position: relative;

  &__input-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__input {
    flex: 1;
    padding-right: 36px !important;
  }

  &__spinner {
    position: absolute;
    right: 12px;
    color: #1ebeaf;
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 100;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10);
    max-height: 220px;
    overflow-y: auto;
  }
}

.c-charity-result {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:last-child { border-bottom: none; }
  &:hover { background: #f0fdfc; }

  &__logo {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    object-fit: contain;
    flex-shrink: 0;
    background: #f5f5f5;
  }

  &__logo-placeholder {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background: #e6f9f7;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1ebeaf;
    flex-shrink: 0;
    font-size: 14px;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: #111;
    font-family: 'Red Hat Display', sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__id {
    font-size: 11px;
    color: #888;
    font-family: 'Red Hat Display', sans-serif;
  }
}
</style>
